import { PartialState } from '../modules/end-user/playground/pages/playground.props';
import { PermissionTypes } from '../utils/interface-types';
import API from './api-middleware';

const devUrl = process.env.REACT_APP_USER_SERVER_URL;
// For Sidebar permissions view
export const hasViewPermission = (moduleName: string, permissions: PermissionTypes[]): boolean => {
  const module = permissions.find(
    (item) =>
      item.module?.toLowerCase().replaceAll(' ', '-') ===
      moduleName.toLowerCase().replaceAll(' ', '-'),
  );

  return module?.data?.includes('view') ?? false;
};

export const verifyAccess = (module: string, navigateTo: string) => {
  const permissionsJson = localStorage.getItem('permissions');
  if (permissionsJson !== null) {
    const parsedPermissions: PermissionTypes[] = JSON.parse(permissionsJson);

    const permittedModules = parsedPermissions.map((permission) => permission.module);
    if (permittedModules.includes(module)) {
      if (hasViewPermission(module, parsedPermissions)) {
        localStorage.setItem('activeRoute', navigateTo);
        return true;
      }
      return false;
    }
  }
  return false;
};

/** CRUD flags from `localStorage.permissions` for a module key (e.g. `team`, `users`, `llm`). */
export function getModuleCrudPermissions(module: string): {
  hasCreate: boolean;
  hasUpdate: boolean;
  hasDelete: boolean;
} {
  try {
    const permissionsJson = localStorage.getItem('permissions');
    if (!permissionsJson) {
      return { hasCreate: false, hasUpdate: false, hasDelete: false };
    }
    const parsed: PermissionTypes[] = JSON.parse(permissionsJson);
    const permittedAccess = parsed.find((p) => p.module === module)?.data ?? [];
    return {
      hasCreate: permittedAccess.includes('create'),
      hasUpdate: permittedAccess.includes('update'),
      hasDelete: permittedAccess.includes('delete'),
    };
  } catch {
    return { hasCreate: false, hasUpdate: false, hasDelete: false };
  }
}

export const saveToLocalStorage = (partialState: PartialState = {}) => {
  const currentState = JSON.parse(localStorage.getItem('playground_state') || '{}');
  const updatedState = { ...currentState, ...partialState };
  localStorage.setItem('playground_state', JSON.stringify(updatedState));
};

export const loadFromLocalStorage = (): PartialState => {
  return JSON.parse(localStorage.getItem('playground_state') || '{}');
};

export const truncateData = (string: string, lengthLimit: number) => {
  if (string?.length > lengthLimit) {
    return `${string?.slice(0, lengthLimit)}...`;
  }
  return string;
};

export const validateName = (name: string) => {
  if (name === '') return 'Name is required!';
  if (!/^[a-zA-Z\s]+$/.test(name)) return 'Name should contain only alphabets!';
  return '';
};

export const validateEmail = (email: string) => {
  if (email === '') return 'Email is required!';

  const emailRegex =
    // eslint-disable-next-line no-useless-escape
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address!';
  }

  const domain = email.split('@')[1]?.toLowerCase();
  const blockedDomains = [
    // "gmail.com",
    'yahoo.com',
    'hotmail.com',
    'outlook.com',
    'live.com',
    'icloud.com',
    'aol.com',
  ];

  if (blockedDomains.includes(domain)) {
    return `Personal email domains like ${blockedDomains.join(
      ', ',
    )} are not allowed. Please use your professional or company email.`;
  }

  return '';
};

export const validateDomainEmail = (email: string) => {
  if (email === '') return 'Email is required!';

  const emailRegex =
    // eslint-disable-next-line no-useless-escape
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  if (email.length > 2 && !email.match(emailRegex)) {
    return 'Please enter a valid email address!';
  }

  const storedEmail = localStorage.getItem('email');
  const storedDomain = storedEmail?.split('@')[1]?.toLowerCase().replace(/^"|"$/g, '');
  const inputDomain = email.split('@')[1]?.toLowerCase();

  if (storedDomain && inputDomain !== storedDomain) {
    return `Email domain must match the original domain: ${storedDomain}`;
  }

  return '';
};

export const validateSelection = (value: number, fieldName: string) => {
  return value === 0 ? `Please select ${fieldName}` : '';
};

export const validateTextField = (value: string, fieldName: string) => {
  if (value === '') return `${fieldName} is required!`;
  if (!/^[a-zA-Z0-9\s._-]+$/.test(value))
    return `${fieldName} should not contains special characters!`;
  return '';
};

export const validateTextFieldForSpecialCharsAndNum = (
  value: string,
  fieldName: string,
  isRequired: boolean = true,
) => {
  if (value === '' && isRequired) return `${fieldName} is required!`;
  if (value && !/^[a-zA-Z\s._-]+$/.test(value))
    return `${fieldName} should not contain numbers or special characters!`;
  return '';
};

export const validateText = (value: string, fieldName: string) => {
  if (!value) return `${fieldName} is required.`;
  return '';
};

export const validateWebsite = (website: string): string => {
  const websiteRegex =
    /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})(\.[a-zA-Z]{2,})?(\/.*)?$/;
  if (website.trim() && !websiteRegex.test(website.trim())) {
    return 'Please enter a valid website URL!';
  }
  return '';
};

export const validateWebsiteSubdomain = (website: string): string => {
  const websiteRegex = /^(https:\/\/|http:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/;
  if (website.trim() && !websiteRegex.test(website.trim())) {
    return 'Please enter a valid website URL!';
  }
  return '';
};

export const validateTextAreaField = (value: string, fieldName: string) => {
  if (value.trim() === '') return '';
  if (!/^[a-zA-Z0-9\s._-]+$/.test(value))
    return `${fieldName} should not contains special characters!`;
  return '';
};

export const validateJSON = (input: string) => {
  try {
    JSON.parse(input);
    return true;
  } catch (error) {
    return false;
  }
};

export const validateAlphabetField = (value: string, fieldName: string) => {
  if (value.trim() === '') return `${fieldName} is required!`;
  if (/\s/.test(value)) return `${fieldName} should not contain spaces!`;
  if (!/^[a-zA-Z]+$/.test(value)) return `${fieldName} should contain only alphabets!`;
  return '';
};

export const validateNumberField = (value: string, fieldName: string) => {
  if (value === '' || value === 'undefined' || value === undefined || value === `${0}`)
    return `${fieldName} is required!`;
  return '';
};

export const validateIP = () => {
  const ipv4Pattern =
    /^(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])$/;
  const ipv6Pattern =
    /^(([0-9a-fA-F]{1,4}:){7}([0-9a-fA-F]{1,4}|:)|(([0-9a-fA-F]{1,4}:){1,7}|:):(([0-9a-fA-F]{1,4}:){1,7}|:)|::|(([0-9a-fA-F]{1,4}:){1,7}|:):(([0-9a-fA-F]{1,4}:){1,7}|:))$/;
  const combinedPattern = new RegExp(`^(${ipv4Pattern.source}|${ipv6Pattern.source})$`);
  return combinedPattern;
};

export const formatTimestamp = (timestamp: string): string => {
  const now = new Date();
  const date = new Date(timestamp);

  const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const inputDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const diffInDays = Math.floor((nowDate.getTime() - inputDate.getTime()) / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';

  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diffInSeconds < 60) return `${diffInSeconds} secs ago`;

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes} mins ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours} hours ago`;

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  return `${day}-${month}-${year} | ${hours}:${minutes} ${period}`;
};

export type LlmPermissionItem = {
  id: number;
  name: string;
  provider_id: number;
  proverID: number;
  provider_name: string;
  supports_tools?: boolean;
  supports_reasoning?: boolean;
  input_price_per_million?: number;
  output_price_per_million?: number;
  model_status?: string;
};

export const ADMIN_DISABLED_MODEL_MESSAGE =
  'This model is disabled by an admin. Select another model to continue.';

export function isAdminBlockedModelStatus(status?: string | null): boolean {
  const normalized = String(status || '')
    .trim()
    .toLowerCase();
  return normalized === 'disabled' || normalized === 'deprecated';
}

let mergedLlmPermissionsCache: LlmPermissionItem[] | null = null;
let loadPermissionsInFlight: Promise<void> | null = null;

export function getBasePermissionsFromStorage(): LlmPermissionItem[] {
  const allPermissions = localStorage.getItem('configurations');
  if (!allPermissions) return [];

  try {
    const chatPermissions = JSON.parse(allPermissions) as {
      models?: Array<{
        id: number | string;
        name: string;
        provider_id: number | string;
        provider_name: string;
      }>;
    };
    if (!chatPermissions?.models || !Array.isArray(chatPermissions.models)) {
      return [];
    }
    return chatPermissions.models.map((item) => {
      const raw = item as {
        id: number | string;
        name: string;
        provider_id?: number | string;
        proverID?: number | string;
        provider_name: string;
        model_status?: string | null;
      };
      const providerId = Number(raw.provider_id ?? raw.proverID);
      const modelStatus = raw.model_status != null ? String(raw.model_status) : undefined;
      return {
        id: Number(raw.id),
        name: String(raw.name ?? ''),
        provider_id: providerId,
        proverID: providerId,
        provider_name: String(raw.provider_name ?? ''),
        model_status: modelStatus,
      };
    });
  } catch {
    return [];
  }
}

/**
 * Loads permissions and merges for the Google/Gemini provider:
 * - If the user has a Gemini key saved (onboarded) AND getGeminiModels returns models:
 *   show only those onboarded Gemini models under Gemini (replace any Google from base).
 * - If the user has no Gemini key saved or no onboarded models:
 *   do not cache; getAllPermissionsOfLlms() will always read from localStorage configurations.
 */
export const loadAllPermissionsOfLlms = async (): Promise<void> => {
  if (mergedLlmPermissionsCache !== null) return;
  if (loadPermissionsInFlight) {
    await loadPermissionsInFlight;
    return;
  }

  loadPermissionsInFlight = (async (): Promise<void> => {
    const base = getBasePermissionsFromStorage();
    try {
      const { getOnboardedGeminiKey } = await import('../modules/end-user/settings/service');
      const keyRes = await getOnboardedGeminiKey();
      const hasSavedKey = Boolean(keyRes?.data?.data?.gemini_key);
      if (!hasSavedKey) {
        // No Gemini key: don't cache so getAllPermissionsOfLlms() always reads from localStorage
        return;
      }
      const { getGeminiModels } = await import('../modules/end-user/llm/services/llm');
      const geminiRes = await getGeminiModels();
      const geminiList = geminiRes?.data?.data?.models ?? geminiRes?.data ?? [];
      const geminiArray = Array.isArray(geminiList) ? geminiList : [];
      if (geminiArray.length > 0) {
        // User has onboarded Gemini: show only these models under Gemini (not base Google)
        const googleProviderId = base.find(
          (p) => p?.provider_name?.toLowerCase() === 'google',
        )?.proverID;
        const googleId =
          googleProviderId ?? Number((geminiArray[0] as { proverID?: number })?.proverID);
        const geminiMapped: LlmPermissionItem[] = geminiArray.map(
          (
            m: { id?: number; name?: string; proverID?: number; provider_id?: number },
            idx: number,
          ) => {
            const providerId = googleId ?? Number(m?.provider_id ?? m?.proverID);
            return {
              id: m?.id ?? 0,
              name: m?.name ?? `Gemini ${idx + 1}`,
              provider_id: providerId,
              proverID: providerId,
              provider_name: 'Google',
            };
          },
        );
        const rest = base.filter((p) => p?.provider_name?.toLowerCase() !== 'google');
        mergedLlmPermissionsCache = [...rest, ...geminiMapped].sort((a, b) =>
          String(a.name).localeCompare(String(b.name)),
        );
      } else {
        // Key saved but no models returned: use base (configurations from localStorage)
        mergedLlmPermissionsCache = [...base];
      }
    } catch {
      // On error: don't cache so getAllPermissionsOfLlms() reads from localStorage
    } finally {
      loadPermissionsInFlight = null;
    }
  })();

  await loadPermissionsInFlight;
};

/**
 * Returns merged LLM permissions (with Gemini for Google when onboarded) if loadAllPermissionsOfLlms
 * has been called; otherwise returns base permissions from localStorage.
 */
export const getAllPermissionsOfLlms = (): LlmPermissionItem[] => {
  if (mergedLlmPermissionsCache !== null) return mergedLlmPermissionsCache;
  return getBasePermissionsFromStorage();
};

/** Preferred OpenAI chat model when the user has access to it. */
export const PREFERRED_OPENAI_CHAT_MODEL_NAME = 'gpt-5-nano-2025-08-07';

/**
 * Picks the default modal for a provider in chat localStorage: OpenAI prefers
 * {@link PREFERRED_OPENAI_CHAT_MODEL_NAME} when present; otherwise first model by name.
 */
export function pickDefaultChatModalForProvider(
  permissions: LlmPermissionItem[],
  providerId: number,
): LlmPermissionItem | undefined {
  const forProvider = permissions.filter((m) => Number(m.proverID) === Number(providerId));
  if (forProvider.length === 0) return undefined;

  const usable = forProvider.filter((m) => !isAdminBlockedModelStatus(m.model_status));
  const pool = usable.length > 0 ? usable : forProvider;

  const isOpenAi = pool.some((m) => m.provider_name?.toLowerCase() === 'openai');
  if (isOpenAi) {
    const preferred = pool.find((m) => m.name === PREFERRED_OPENAI_CHAT_MODEL_NAME);
    if (preferred) return preferred;
  }

  return [...pool].sort((a, b) => String(a.name).localeCompare(String(b.name)))[0];
}

/** Event name dispatched when merged LLM permissions (e.g. onboarded Gemini) are updated. */
export const LLM_PERMISSIONS_INVALIDATED_EVENT = 'llm-permissions-invalidated';

/** Clears the merged permissions cache so the next read uses fresh data. */
export function invalidateMergedLlmPermissionsCache(): void {
  mergedLlmPermissionsCache = null;
}

/**
 * Invalidates the cache, reloads merged permissions (including onboarded Gemini),
 * and dispatches LLM_PERMISSIONS_INVALIDATED_EVENT so consumers can refresh their UI.
 * Call this after onboarding/updating Gemini models or saving/deleting the Gemini key.
 */
export async function refreshMergedLlmPermissions(): Promise<void> {
  invalidateMergedLlmPermissionsCache();
  await loadAllPermissionsOfLlms();
  window.dispatchEvent(new CustomEvent(LLM_PERMISSIONS_INVALIDATED_EVENT));
}

export const getAllPermissionsOfEmbeddedLlms = () => {
  const allPermissions = localStorage.getItem('configurations');
  if (allPermissions) {
    const chatPermissions = JSON.parse(allPermissions);
    if (chatPermissions) {
      return chatPermissions?.embedding_models?.map(
        (item: {
          id: number | string;
          name: string;
          provider_id: number | string;
          embedding_provider_name: string;
        }) => ({
          id: item.id,
          name: item.name,
          proverID: item.provider_id,
          provider_name: item.embedding_provider_name,
        }),
      );
    }
    return [];
  }
  return [];
};

// budget apps - optional filters (e.g. team_id, providers) to match gateway list filter
export const getBudget = async (
  filters?: Array<{ type: string; value: number | string | number[] }>,
) => {
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const query = filters?.length ? `?filters=${encodeURIComponent(JSON.stringify(filters))}` : '';
    const response = await API(devUrl).get(`accounts${query}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

const providerColorMap = new Map();
const generateRandomColor = () => {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i += 1) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

export const getColorByProvider = (providerName: string) => {
  if (!providerName) {
    return '#000000';
  }
  const provider = providerName.toLowerCase();
  if (providerColorMap.has(provider)) {
    return providerColorMap.get(provider);
  }

  let newColor;
  do {
    newColor = generateRandomColor();
  } while (Array.from(providerColorMap.values()).includes(newColor));
  providerColorMap.set(provider, newColor);
  return newColor;
};

export type Permission = {
  module: string;
  data: Array<'create' | 'update' | 'delete' | 'view'>;
  llm: number[];
};

export const redirectToFirstPermittedModule = (
  isSuperAdmin: boolean,
  permissions: Permission[],
  navigate: (path: string) => void,
) => {
  if (isSuperAdmin) {
    navigate('/system-admin');
    return;
  }

  const modules = [
    { name: 'dashboard', path: '/dashboard' },
    { name: 'chat', path: '/chat' },
    { name: 'users', path: '/users' },
    { name: 'roles', path: '/roles' },
    { name: 'teams', path: '/teams' },
    { name: 'llm', path: '/llm' },
    { name: 'assistant', path: '/all-assistants' },
    { name: 'gateway', path: '/gateway' },
    { name: 'agents', path: '/agents' },
    { name: 'Playground', path: '/playground' },
    { name: 'Automator', path: '/automator' },
    { name: 'Knowledge Base', path: '/knowledge-base' },
    { name: 'Embedding Config', path: '/embedding-config' },
    { name: 'Budget Settings', path: '/budget-settings' },
    { name: 'Prompt Optimization Configuration', path: '/budget-settings' },
    { name: 'Chat PII', path: '/pii-config' },
  ];

  const permittedModules = permissions.map((p) => p.module);

  // eslint-disable-next-line no-restricted-syntax
  for (const module of modules) {
    if (permittedModules.includes(module.name) && hasViewPermission(module.name, permissions)) {
      navigate(module.path);
      return;
    }
  }
};

export function getLastMonthsInfo(
  year: number,
  month: number,
  count: number,
): { labels: string[]; keys: string[] } {
  const MONTH_NAMES_SHORT = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const labels: string[] = [];
  const keys: string[] = [];

  for (let i = count - 1; i >= 0; i -= 1) {
    let m = month - i;
    let y = year;
    if (m <= 0) {
      m += 12;
      y -= 1;
    }
    labels.push(MONTH_NAMES_SHORT[m - 1]);
    keys.push(`${y}-${m.toString().padStart(2, '0')}`);
  }

  return { labels, keys };
}

export function getAuthHeaders(): { Authorization: string } {
  let token = localStorage.getItem('token');
  if (token !== null) {
    token = JSON.parse(token);
  }
  return { Authorization: `Bearer ${token}` };
}

export type TeamListItem = { id: number; name: string; account_id?: number };

export type GetTeamsParams = {
  limit?: number;
  offset?: number;
  /** Optional server-side name search */
  search?: string;
};

/** GET /teams — paginated body shape: `{ teams, total, limit?, offset? }` under `data`. */
export const getTeams = async (params?: GetTeamsParams) => {
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const searchParams = new URLSearchParams();
    if (params?.limit != null) searchParams.set('limit', String(params.limit));
    if (params?.offset != null) searchParams.set('offset', String(params.offset));
    if (params?.search != null && params.search.trim() !== '') {
      searchParams.set('search', params.search.trim());
    }
    const query = searchParams.toString();
    const url = query ? `teams?${query}` : 'teams';
    const response = await API(devUrl).get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches every team page for dropdowns (uses `total` from the API when present).
 * Returns HTTP status from the first request for 401 handling.
 */
export async function getAllTeamsList() {
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const url = `teams?all=true`;
    const response = await API(devUrl).get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
}

export const getMaskedKey = (token?: string) => {
  if (!token) return '—';

  const last4 = token.slice(-4);
  return `****${last4}`;
};

/** Decode common HTML entities so quotes and ampersands display correctly */
export const decodeHtmlEntities = (text: string): string => {
  if (typeof text !== 'string') return text;
  return text
    .replace(/&quot;/g, '"')
    .replace(/&#34;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&#38;/g, '&')
    .replace(/&#x27;/g, "'")
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(parseInt(dec, 10)));
};
